/* eslint-disable no-param-reassign */
import { get, isNumber, isUndefined, pickBy } from 'lodash';
import { transformRequestQueryParams } from '@core/common/transformers/transform-request-query-params';
import { METRIC_NAMES, RESOURCES_CONFIGURATION_INTERVAL as INTERVAL, USER_TYPES_NAMES, AGENT_STATUSES } from '@config/Consts';
import { makeArrayOfStrings } from '@core/utils/utils';
import AgentViewModel from '@core/models/AgentViewModel';
import AgentManagerMetricsModel from '@core/models/AgentManagerMetricsModel';
import BaseClient from '@core/common/BaseClient';
import { METRICS_RESOURCE_PARAMS, RESOURCES as AGENT_MANAGER_RESOURCES } from '@core/clients/AgentManagerClient';

const DEFAULT_STATUSES_TO_RETRIEVE = [
  AGENT_STATUSES.ONLINE,
  AGENT_STATUSES.BACK_SOON,
  AGENT_STATUSES.AWAY,
];

const AGENT_VIEW_API_METRICS_CONSTS = {
  // CURRENT VALUE
  [METRIC_NAMES.AGENT_VIEW_ASSIGNED_CONVERSATIONS]: 'assigned_conversations',
  [METRIC_NAMES.AGENT_VIEW_AGENT_STATUS]: 'agent_current_status',
  [METRIC_NAMES.AGENT_VIEW_STATUS_REASON]: 'agent_current_status_with_reason',
  [METRIC_NAMES.AGENT_VIEW_ACTIVE_CONVERSATIONS]: 'active_conversations',
  [METRIC_NAMES.AGENT_VIEW_AGENT_LOAD]: 'agent_load',
  [METRIC_NAMES.AGENT_VIEW_MAX_SLOTS]: 'max_slots',
  [METRIC_NAMES.AGENT_VIEW_AGENT_STATUS_DURATION]: 'agent_current_status_start_time',
  [METRIC_NAMES.AGENT_VIEW_AGENT_STATUS_WITH_REASON_DURATION]: 'agent_current_status_reason_start_time',

  // BY TIME
  [METRIC_NAMES.AGENT_VIEW_CSAT]: 'csat',
  [METRIC_NAMES.AGENT_VIEW_CLOSED_CONVERSATIONS]: 'closed_conversations',
  [METRIC_NAMES.AGENT_VIEW_TRANSFER_RATE]: 'transfer_rate',
  [METRIC_NAMES.AGENT_VIEW_ONLINE_RATE]: 'online_rate',
};

function prepareAgentsListRequest(settings, httpMethod, params, queryParams = {}) {
  return {
    method: httpMethod,
    interval: INTERVAL.GENERAL_RESOURCE,
    body: params,
    config: {
      params: queryParams,
    },
  };
}

// REQUEST TRANSFORMERS
function prepareAgentViewListRequest(settings, httpMethod, params, queryParams = {}) {
  return {
    method: httpMethod,
    interval: INTERVAL.GENERAL_RESOURCE,
    body: params,
    config: {
      params: queryParams,
    },
  };
}

function transformAgentViewRequest(settings = {}) {
  const params = {
    filters: {
      userTypes: [
        USER_TYPES_NAMES.BOT,
        USER_TYPES_NAMES.HUMAN
      ],
      effectiveAgentStatus: DEFAULT_STATUSES_TO_RETRIEVE,
      time: {
        // setting values for the interceptor to fill
        from: -1,
        to: -1,
      },
    },
    includeAgentMetadata: true,
    metricsToRetrieveByTime: [
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_TRANSFER_RATE],
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_CSAT],
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_CLOSED_CONVERSATIONS],
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_ONLINE_RATE],
    ],
    metricsToRetrieveCurrentValue: [
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_ASSIGNED_CONVERSATIONS],
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_AGENT_STATUS],
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_STATUS_REASON],
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_ACTIVE_CONVERSATIONS],
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_AGENT_LOAD],
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_MAX_SLOTS],
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_AGENT_STATUS_DURATION],
      AGENT_VIEW_API_METRICS_CONSTS[METRIC_NAMES.AGENT_VIEW_AGENT_STATUS_WITH_REASON_DURATION],
    ],
  };
  const queryParams = {};

  const { filter } = settings;
  const { status, agents, skills, groupIds } = filter;

  if (status && status.length) {
    params.filters.effectiveAgentStatus = status;
  }

  if (agents && agents.length) { // table filter
    params.filters.agentIds = agents;
  }

  if (skills && skills.length) {
    params.filters.agentSkillIds = makeArrayOfStrings(skills);
  }

  if (groupIds && groupIds.length) {
    params.filters.agentGroupIds = groupIds;
  }

  // const currentAgentIds = getCurrentPageAgentsIds(settings);
  // if (currentAgentIds) {
  //   params.agentIds = currentAgentIds;
  // }

  return prepareAgentViewListRequest(
    settings,
    BaseClient.HTTP_METHODS.POST,
    params,
    transformRequestQueryParams(queryParams, settings),
  );
}

function transformMetricsRequest(settings) {
  const params = {
    filters: {
      time: {
        // setting values for the interceptor to fill
        from: -1,
        to: -1,
      },
    },
    metricsToRetrieveCurrentValue: [
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_CURRENT_VALUE.ONLINE_AGENTS,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_CURRENT_VALUE.BACK_SOON_AGENTS,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_CURRENT_VALUE.AWAY_AGENTS,
    ],
  };

  const { filter } = settings;
  const { agents: agentsFilter, skills: skillsFilter, groupIds: groupsFilter } = filter;

  if (agentsFilter && agentsFilter.length > 0) {
    params.filters.agentIds = agentsFilter;
  }
  if (groupsFilter && groupsFilter.length > 0) {
    params.filters.agentGroupIds = makeArrayOfStrings(groupsFilter);
  }
  if (skillsFilter && skillsFilter.length > 0) {
    params.filters.skillIds = makeArrayOfStrings(skillsFilter);
  }

  return prepareAgentsListRequest(settings, BaseClient.HTTP_METHODS.POST, params);
}

function transformAgentViewResponse(response) {
  let data = null;
  const agentViewRecords = get(response, 'agentsListResponse.data.agentViewRecords');
  const convertToPercentages = (num = 0) => Math.round(num * 100);

  if (agentViewRecords) {
    data = [];
    agentViewRecords.forEach((record) => {
      const agentMetadata = record.agentMetadata || {};
      const agentRecord = {
        id: record.agentId,
        metrics: new AgentViewModel({
          [METRIC_NAMES.AGENT_VIEW_TRANSFER_RATE]: convertToPercentages(record.transferRate),
          [METRIC_NAMES.AGENT_VIEW_ASSIGNED_CONVERSATIONS]:
          record[METRIC_NAMES.AGENT_VIEW_ASSIGNED_CONVERSATIONS] || 0,
          [METRIC_NAMES.AGENT_VIEW_AGENT_STATUS]: record[METRIC_NAMES.AGENT_VIEW_AGENT_STATUS],
          [METRIC_NAMES.AGENT_VIEW_STATUS_REASON]: record[METRIC_NAMES.AGENT_VIEW_STATUS_REASON],
          [METRIC_NAMES.AGENT_VIEW_AGENT_NAME]: agentMetadata[METRIC_NAMES.AGENT_VIEW_AGENT_NAME],
          [METRIC_NAMES.AGENT_VIEW_AGENT_GROUP]: agentMetadata[METRIC_NAMES.AGENT_VIEW_AGENT_GROUP],
          [METRIC_NAMES.AGENT_VIEW_AGENT_SKILLS]: agentMetadata[METRIC_NAMES.AGENT_VIEW_AGENT_SKILLS],
          [METRIC_NAMES.AGENT_VIEW_AGENT_PICTURE]: agentMetadata[METRIC_NAMES.AGENT_VIEW_AGENT_PICTURE] || '',
          [METRIC_NAMES.AGENT_VIEW_ACTIVE_CONVERSATIONS]: record[METRIC_NAMES.AGENT_VIEW_ACTIVE_CONVERSATIONS] || 0,
          [METRIC_NAMES.AGENT_VIEW_AGENT_LOAD]: convertToPercentages(record[METRIC_NAMES.AGENT_VIEW_AGENT_LOAD]),
          [METRIC_NAMES.AGENT_VIEW_MAX_SLOTS]: record[METRIC_NAMES.AGENT_VIEW_MAX_SLOTS] || 0,
          [METRIC_NAMES.AGENT_VIEW_CSAT]: isNumber(record[METRIC_NAMES.AGENT_VIEW_CSAT]) ? convertToPercentages(record[METRIC_NAMES.AGENT_VIEW_CSAT]) : null,
          [METRIC_NAMES.AGENT_VIEW_CLOSED_CONVERSATIONS]: record[METRIC_NAMES.AGENT_VIEW_CLOSED_CONVERSATIONS] || 0,
          [METRIC_NAMES.AGENT_VIEW_AGENT_STATUS_DURATION]: Date.now() - record[METRIC_NAMES.AGENT_VIEW_AGENT_STATUS_DURATION],
          [METRIC_NAMES.AGENT_VIEW_AGENT_STATUS_WITH_REASON_DURATION]: Date.now() - record[METRIC_NAMES.AGENT_VIEW_AGENT_STATUS_WITH_REASON_DURATION],
          [METRIC_NAMES.AGENT_VIEW_ONLINE_RATE]: convertToPercentages(record[METRIC_NAMES.AGENT_VIEW_ONLINE_RATE]),
        }),
      };
      data.push(agentRecord);
    });
  }
  const agentsCount = get(response, 'agentsListResponse.data.metadata.count');
  return { agentsCount, data };
}

function transformMetricsResponse(response) {
  let data = null;
  const agentStatusRecord = get(response, 'agentsListResponse.data.all');
  if (!isUndefined(agentStatusRecord)) {
    data = new AgentManagerMetricsModel({
      [METRIC_NAMES.AGENT_VIEW_METRICS_AWAY_AGENTS]: agentStatusRecord.awayAgents || 0,
      [METRIC_NAMES.AGENT_VIEW_METRICS_BACK_SOON_AGENTS]: agentStatusRecord.backSoonAgents || 0,
      [METRIC_NAMES.AGENT_VIEW_METRICS_ONLINE_AGENTS]: agentStatusRecord.onlineAgents || 0,
    });
  }

  return data && pickBy(data, v => v !== undefined);
}

const RESOURCE_TRANSFORM_REQUEST_METHOD_MAP = {
  [AGENT_MANAGER_RESOURCES.AGENT_VIEW]: transformAgentViewRequest,
  [AGENT_MANAGER_RESOURCES.METRICS]: transformMetricsRequest,
};

const RESOURCE_TRANSFORM_RESPONSE_METHOD_MAP = {
  [AGENT_MANAGER_RESOURCES.AGENT_VIEW]: transformAgentViewResponse,
  [AGENT_MANAGER_RESOURCES.METRICS]: transformMetricsResponse,
};

function transformRequest(resourceName, settings) {
  return RESOURCE_TRANSFORM_REQUEST_METHOD_MAP[resourceName](settings);
}

function transformResponse(resourceName, response) {
  return RESOURCE_TRANSFORM_RESPONSE_METHOD_MAP[resourceName](response);
}

export default {
  transformRequest,
  transformResponse,
  AGENT_VIEW_API_METRICS_CONSTS,
  DEFAULT_STATUSES_TO_RETRIEVE,
};
